import { HttpStatusCode } from '@angular/common/http';
import { MonoTypeOperatorFunction, Observable, retry, throwError, timer } from 'rxjs';
import { ADYEN_ERROR_CONSTANTS } from '../constants/adyen-error.constants';

/** The notice shown when the booking failed after the customer paid: the money comes back. */
export const REFUND_NOTICE_KEY = 'payment_components.refundNotice';

/** The notice shown when the booking's answer never arrived: the booking may or may not exist. */
const OUTCOME_UNKNOWN_NOTICE_KEY = 'payment_components.bookingOutcomeUnknownNotice';

/** The part of an HttpErrorResponse these helpers read. */
type BookingErrorLike = { status?: number; error?: { errors?: unknown } | null } | null | undefined;

/**
 * Whether a failed booking request's answer was LOST rather than given.
 *
 * A booking the server REFUSED (any real HTTP answer) did not happen. A request whose answer went
 * missing - the connection dropped (status 0) or a gateway gave up waiting for the backend (504) -
 * may or may not have been booked before the answer was lost. Angular's HttpClient reports both
 * as an HttpErrorResponse carrying that status, which is all this looks at.
 */
export function isBookingOutcomeUnknown(error: { status?: number } | null | undefined): boolean {
  return error?.status === 0 || error?.status === HttpStatusCode.GatewayTimeout;
}

/**
 * The translation key the backend sent beside its message (`errors[0].key`), if any. The errors
 * list is sometimes an object keyed "Item:N" instead of an array; only the array form carries keys.
 */
export function backendErrorKey(error: BookingErrorLike): string | undefined {
  const errors = error?.error?.errors;
  const key = Array.isArray(errors) ? (errors[0] as { key?: unknown } | undefined)?.key : undefined;

  return typeof key === 'string' ? key : undefined;
}

/**
 * The values the backend sent beside its key (`errors[0].params`), or an empty object when there
 * are none. Read only from the array form of the errors list, like the key.
 */
function backendErrorParams(error: BookingErrorLike): Record<string, unknown> {
  const errors = error?.error?.errors;
  const params = Array.isArray(errors) ? (errors[0] as { params?: unknown } | undefined)?.params : undefined;

  return params && typeof params === 'object' ? params as Record<string, unknown> : {};
}

/**
 * The backend refused the payment session because it ALREADY backs a confirmed booking of this
 * partner (`params.already_booked = 1`). Unlike a bare spent session this is proof the booking
 * exists: the customer must be told so, and never asked to pay again.
 */
export function isAlreadyBookedWithPayment(error: BookingErrorLike): boolean {
  const { BACKEND_KEYS, ALREADY_BOOKED_PARAM } = ADYEN_ERROR_CONSTANTS;
  const alreadyBooked = backendErrorParams(error)[ALREADY_BOOKED_PARAM];

  return backendErrorKey(error) === BACKEND_KEYS.SESSION_ALREADY_USED
    && (alreadyBooked === 1 || alreadyBooked === '1' || alreadyBooked === true);
}

/** Another /book request for the same slot held the booking lock (HTTP 423). */
export function isBookingLocked(error: { status?: number } | null | undefined): boolean {
  return error?.status === HttpStatusCode.Locked;
}

/**
 * Sends a card-paid /book again while it is answered "booking lock" (423), until the lock has
 * certainly expired.
 *
 * The backend keeps the slot on a 10 s lock that is never released early - the request holding it
 * is quite possibly this payment's own first attempt (a double submit, a reload during /book).
 * Showing the lock as an error told a customer whose booking exists that it failed, and invited a
 * second payment. So the SAME request is sent again at each of `resendAtMs` (counted from the first
 * lock answer) and the first answer that is not a lock is the answer: it books, or says that this
 * payment already backs a booking (isAlreadyBookedWithPayment), or fails like any other refusal.
 * A lock after the last resend is handed on - see isPaidBookingStillCompleting. Never for anything
 * but a lock, and never for an unpaid booking.
 */
export function resendPaidBookingWhileLocked<T>(
  paid: boolean,
  resendAtMs: readonly number[] = ADYEN_ERROR_CONSTANTS.BOOKING_LOCK_RESEND_AT_MS
): MonoTypeOperatorFunction<T> {
  return (source: Observable<T>) => !paid
    ? source
    : source.pipe(
      retry({
        count: resendAtMs.length,
        delay: (error: { status?: number } | null | undefined, retryCount: number) =>
          isBookingLocked(error)
            ? timer(resendAtMs[retryCount - 1] - (resendAtMs[retryCount - 2] ?? 0))
            : throwError(() => error),
      })
    );
}

/**
 * A card-paid /book was still answered "booking lock" after every resend. The lock was most likely
 * this payment's own first request, so the booking may well exist: the customer is told it is
 * being completed and to check their email - never offered a new payment.
 */
export function isPaidBookingStillCompleting(error: { status?: number } | null | undefined, paid: boolean): boolean {
  return paid && isBookingLocked(error);
}

/**
 * The backend will not claim this payment session for a booking, and does not say why. That is
 * NOT proof a booking exists: it answers the same for a session that belongs to no log, is being
 * refunded, or has expired. So the customer is told to check their email first, exactly as after a
 * lost answer, and re-arms the payment with "Pay again" only once they have.
 *
 * Never true when the backend says the session already backs a booking
 * (isAlreadyBookedWithPayment): that booking exists, so "Pay again" must never be offered beside it.
 */
export function isPaymentSessionSpent(error: BookingErrorLike): boolean {
  return backendErrorKey(error) === ADYEN_ERROR_CONSTANTS.BACKEND_KEYS.SESSION_ALREADY_USED
    && !isAlreadyBookedWithPayment(error);
}

/**
 * The widget text for a keyed Adyen refusal, or undefined for no key / a key this widget does not
 * know yet - the caller then shows what it showed before keys existed.
 *
 * `payment_unavailable` depends on the status: 503 heals on its own (try again), 400 does not.
 */
export function adyenErrorMessageKey(error: BookingErrorLike): string | undefined {
  const { BACKEND_KEYS, MESSAGE_KEYS } = ADYEN_ERROR_CONSTANTS;
  switch (backendErrorKey(error)) {
    case BACKEND_KEYS.BOOKING_REFUSED:
      return MESSAGE_KEYS.BOOKING_REFUSED;
    case BACKEND_KEYS.PAYMENT_UNAVAILABLE:
      return error?.status === HttpStatusCode.ServiceUnavailable
        ? MESSAGE_KEYS.TEMPORARILY_UNAVAILABLE
        : MESSAGE_KEYS.UNAVAILABLE;
    case BACKEND_KEYS.NOT_ENABLED_FOR_BOOKING:
      return MESSAGE_KEYS.UNAVAILABLE;
    case BACKEND_KEYS.SESSION_EXPIRED:
      return MESSAGE_KEYS.SESSION_EXPIRED;
    default:
      return undefined;
  }
}

/**
 * Which notice a paid booking that errored should show.
 *
 * A refusal is reversed, so the refund notice is true. After a lost answer, promising a refund is
 * false, and it invites the customer to pay a second time for an appointment that may already
 * exist - so that case gets the "check your email" notice instead. A spent payment session gets
 * its own "check your email" notice: whether a booking stands behind it is not known here - unless
 * the backend says the session already backs a booking, which gets the "already booked" notice.
 * A card-paid booking still held by the booking lock after every resend is most likely being
 * completed, and gets its own "check your email" notice (`paidWithAdyen`: only that payment resends).
 */
export function refundNoticeKeyFor(error: BookingErrorLike, paidWithAdyen = false): string {
  if (isPaidBookingStillCompleting(error, paidWithAdyen)) {
    return ADYEN_ERROR_CONSTANTS.STILL_COMPLETING_NOTICE_KEY;
  }
  if (isAlreadyBookedWithPayment(error)) {
    return ADYEN_ERROR_CONSTANTS.ALREADY_BOOKED_NOTICE_KEY;
  }
  if (isPaymentSessionSpent(error)) {
    return ADYEN_ERROR_CONSTANTS.SESSION_SPENT_NOTICE_KEY;
  }

  return isBookingOutcomeUnknown(error) ? OUTCOME_UNKNOWN_NOTICE_KEY : REFUND_NOTICE_KEY;
}

